import React, { useState, useEffect, useCallback } from 'react';
import { 
  DollarSign, TrendingUp, TrendingDown, AlertTriangle, CreditCard,
  Plus, Search, Filter, X, Edit, Trash2, Check, ExternalLink,
  FileText, Receipt, Calculator, PieChart, RefreshCw, Send,
  ChevronDown, Calendar, Building, Clock, AlertCircle, Mail, Bell
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Constants
const INVOICE_STATUSES = ['Pending', 'Paid', 'Partial', 'Overdue'];
const BILL_STATUSES = ['Current', 'Upcoming', 'Future', 'Paid', 'Overdue'];
const BILL_CATEGORIES = [
  'Rent/Lease', 'Utilities', 'Payroll', 'Software/Subscriptions',
  'Marketing', 'Insurance', 'Professional Services', 'Equipment',
  'Travel', 'Miscellaneous'
];
const BILL_FREQUENCIES = ['One-Time', 'Weekly', 'Monthly', 'Quarterly', 'Annually'];
const PAYMENT_METHODS = ['Check', 'ACH/Wire', 'Credit Card', 'Cash', 'Zelle', 'CashApp', 'Stripe', 'Other'];
const BAD_DEBT_REASONS = [
  'Client Unresponsive', 'Client Bankruptcy', 'Disputed/Uncollectible',
  'Settlement Accepted', 'Statute of Limitations', 'Other'
];
const PERIOD_OPTIONS = [
  { value: '', label: 'All Time' },
  { value: 'this_month', label: 'This Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'this_year', label: 'This Year' }
];

// Helper functions
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// ============== FINANCIAL DASHBOARD ==============
export const FinancialDashboard = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [period, setPeriod] = useState('');
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [upcomingBills, setUpcomingBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [runningJob, setRunningJob] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const periodQuery = period ? `?period=${period}` : '';
      const [statsRes, invoicesRes, billsRes, schedulerRes] = await Promise.all([
        axios.get(`${API}/admin/financial-stats${periodQuery}`, { headers }),
        axios.get(`${API}/admin/invoices?status=Pending`, { headers }),
        axios.get(`${API}/admin/bills?status=Current`, { headers }),
        axios.get(`${API}/admin/scheduler/status`, { headers }).catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setRecentInvoices(invoicesRes.data.slice(0, 5));
      setUpcomingBills(billsRes.data.slice(0, 5));
      setSchedulerStatus(schedulerRes.data);
    } catch (error) {
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  }, [token, period]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const openStripeDashboard = async () => {
    try {
      const res = await axios.get(`${API}/admin/stripe/dashboard-url`, { headers });
      window.open(res.data.url, '_blank');
    } catch (error) {
      window.open('https://dashboard.stripe.com', '_blank');
    }
  };

  const runReminderJobNow = async () => {
    try {
      setRunningJob(true);
      await axios.post(`${API}/admin/scheduler/run-now`, {}, { headers });
      toast.success('Reminder job executed! Check activities for results.');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to run reminder job');
    } finally {
      setRunningJob(false);
    }
  };

  if (loading && !stats) {
    return <div className="text-white text-center py-12">Loading financial data...</div>;
  }

  return (
    <div className="space-y-6" data-testid="financial-dashboard">
      {/* Header with Period Filter */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white">Financial Dashboard</h2>
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-3 py-2 bg-[#234b8a] border border-white/20 text-white rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
            data-testid="period-filter"
          >
            {PERIOD_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button
            onClick={openStripeDashboard}
            className="flex items-center gap-2 px-4 py-2 bg-[#635BFF] text-white rounded hover:bg-[#5851ea] transition-colors text-sm"
            data-testid="stripe-dashboard-btn"
          >
            <CreditCard size={16} />
            Stripe Dashboard
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <KPICard
          label="Total Income"
          value={formatCurrency(stats?.total_income)}
          subtext="Payments Received"
          color="#22c55e"
          icon={TrendingUp}
        />
        <KPICard
          label="Total Expenses"
          value={formatCurrency(stats?.total_expenses)}
          subtext="Bills Paid"
          color="#ef4444"
          icon={TrendingDown}
        />
        <KPICard
          label="Net Profit/Loss"
          value={formatCurrency(stats?.net_profit)}
          subtext="Income - Expenses - Bad Debt"
          color="#C8952A"
          icon={DollarSign}
          highlight={stats?.net_profit < 0}
        />
        <KPICard
          label="Outstanding"
          value={formatCurrency(stats?.outstanding)}
          subtext="Unpaid Invoices"
          color="#f59e0b"
          icon={Clock}
        />
        <KPICard
          label="Bad Debt"
          value={formatCurrency(stats?.total_bad_debt)}
          subtext="Written Off"
          color="#8b5cf6"
          icon={AlertTriangle}
        />
      </div>

      {/* Recent Activity Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Invoices */}
        <div className="bg-[#234b8a] border border-white/20 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <h3 className="text-white font-medium flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>
              Recent Pending Invoices
            </h3>
            <span className="text-[#b8c9e2] text-xs">{stats?.invoice_count || 0} total</span>
          </div>
          <div className="p-4">
            {recentInvoices.length > 0 ? (
              <div className="space-y-2">
                {recentInvoices.map((inv) => (
                  <div key={inv.id} className="flex items-center justify-between p-3 bg-[#1a3a6e] rounded">
                    <div>
                      <p className="text-white text-sm font-medium">{inv.client_name}</p>
                      <p className="text-[#b8c9e2] text-xs">{inv.invoice_number}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white text-sm">{formatCurrency(inv.amount)}</p>
                      <StatusBadge status={inv.status} type="invoice" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#b8c9e2] text-sm text-center py-4">No pending invoices</p>
            )}
          </div>
        </div>

        {/* Upcoming Bills */}
        <div className="bg-[#234b8a] border border-white/20 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <h3 className="text-white font-medium flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-red-500"></span>
              Upcoming Bills
            </h3>
            <span className="text-[#b8c9e2] text-xs">{stats?.bill_count || 0} total</span>
          </div>
          <div className="p-4">
            {upcomingBills.length > 0 ? (
              <div className="space-y-2">
                {upcomingBills.map((bill) => (
                  <div key={bill.id} className="flex items-center justify-between p-3 bg-[#1a3a6e] rounded">
                    <div>
                      <p className="text-white text-sm font-medium">{bill.payee}</p>
                      <p className="text-[#b8c9e2] text-xs">{bill.category}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white text-sm">{formatCurrency(bill.amount)}</p>
                      <p className="text-[#b8c9e2] text-xs">Due: {formatDate(bill.due_date)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#b8c9e2] text-sm text-center py-4">No upcoming bills</p>
            )}
          </div>
        </div>
      </div>

      {/* Automated Reminders Section */}
      {schedulerStatus && (
        <div className="bg-[#234b8a] border border-white/20 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <h3 className="text-white font-medium flex items-center gap-2">
              <Bell size={16} className="text-orange-400" />
              Automated Reminders
            </h3>
            <span className={`text-xs px-2 py-1 rounded ${schedulerStatus.running ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
              {schedulerStatus.running ? 'Active' : 'Inactive'}
            </span>
          </div>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white text-sm">Daily overdue invoice reminders</p>
                <p className="text-[#b8c9e2] text-xs mt-1">
                  Next run: {schedulerStatus.jobs?.[0]?.next_run 
                    ? new Date(schedulerStatus.jobs[0].next_run).toLocaleString() 
                    : 'Not scheduled'}
                </p>
                <p className="text-[#b8c9e2] text-xs">
                  Schedule: {schedulerStatus.reminder_time}
                </p>
              </div>
              <button
                onClick={runReminderJobNow}
                disabled={runningJob}
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors disabled:opacity-50 text-sm"
                data-testid="run-reminder-now-btn"
              >
                {runningJob ? (
                  <RefreshCw size={16} className="animate-spin" />
                ) : (
                  <Bell size={16} />
                )}
                Run Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const KPICard = ({ label, value, subtext, color, icon: Icon, highlight }) => (
  <div className={`bg-[#234b8a] border border-white/20 rounded-lg p-4 relative overflow-hidden ${highlight ? 'border-red-500/50' : ''}`}>
    <div className="absolute top-0 left-0 right-0 h-1" style={{ backgroundColor: color }}></div>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[#b8c9e2] text-xs uppercase tracking-wide">{label}</p>
        <p className="text-xl font-semibold text-white mt-1" style={{ color: highlight ? '#ef4444' : 'white' }}>{value}</p>
        <p className="text-[#b8c9e2] text-xs mt-1">{subtext}</p>
      </div>
      <div className="p-2 rounded" style={{ backgroundColor: color + '20' }}>
        <Icon size={18} style={{ color }} />
      </div>
    </div>
  </div>
);

// ============== INVOICE MANAGEMENT ==============
export const InvoiceManagement = ({ token }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('');
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderInvoice, setReminderInvoice] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API}/admin/invoices`;
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (periodFilter) params.append('period', periodFilter);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await axios.get(url, { headers });
      setInvoices(res.data);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, periodFilter]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleSave = async (data) => {
    try {
      if (editingInvoice) {
        await axios.put(`${API}/admin/invoices/${editingInvoice.id}`, data, { headers });
        toast.success('Invoice updated');
      } else {
        await axios.post(`${API}/admin/invoices`, data, { headers });
        toast.success('Invoice created');
      }
      setShowModal(false);
      setEditingInvoice(null);
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to save invoice');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await axios.delete(`${API}/admin/invoices/${id}`, { headers });
      toast.success('Invoice deleted');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const handleMarkPaid = async (id) => {
    try {
      await axios.post(`${API}/admin/invoices/${id}/mark-paid`, {}, { headers });
      toast.success('Invoice marked as paid');
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to mark invoice as paid');
    }
  };

  const handleStripeCharge = (invoice) => {
    setSelectedInvoice(invoice);
    setShowStripeModal(true);
  };

  const handleSendReminder = (invoice) => {
    setReminderInvoice(invoice);
    setShowReminderModal(true);
  };

  const handleCheckOverdue = async () => {
    try {
      const res = await axios.post(`${API}/admin/invoices/check-overdue`, {}, { headers });
      toast.success(res.data.message);
      fetchInvoices();
    } catch (error) {
      toast.error('Failed to check overdue invoices');
    }
  };

  const filteredInvoices = invoices.filter(inv => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      inv.client_name?.toLowerCase().includes(search) ||
      inv.invoice_number?.toLowerCase().includes(search) ||
      inv.service_description?.toLowerCase().includes(search)
    );
  });

  // Calculate summary
  const summary = {
    total: filteredInvoices.reduce((sum, inv) => sum + (inv.amount || 0), 0),
    paid: filteredInvoices.reduce((sum, inv) => sum + (inv.amount_paid || 0), 0),
    outstanding: filteredInvoices.reduce((sum, inv) => sum + ((inv.amount || 0) - (inv.amount_paid || 0)), 0),
    overdue: filteredInvoices.filter(inv => inv.status === 'Overdue').reduce((sum, inv) => sum + ((inv.amount || 0) - (inv.amount_paid || 0)), 0)
  };

  return (
    <div className="space-y-4" data-testid="invoice-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Invoice Management</h2>
          <p className="text-[#b8c9e2] text-sm">Create, track & manage client invoices</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCheckOverdue}
            className="flex items-center gap-2 px-3 py-2 bg-orange-600/20 text-orange-400 border border-orange-500/30 rounded hover:bg-orange-600/30 transition-colors text-sm"
            title="Check for overdue invoices"
            data-testid="check-overdue-btn"
          >
            <Bell size={16} />
            Check Overdue
          </button>
          <button
            onClick={() => { setEditingInvoice(null); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-[#C8952A] text-white rounded hover:bg-[#b8862a] transition-colors"
            data-testid="new-invoice-btn"
          >
            <Plus size={18} />
            New Invoice
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#234b8a] border border-white/20 rounded-lg p-4">
        {/* Status Tabs */}
        <div className="flex gap-1 mb-4 border-b border-white/10 pb-3">
          {['all', ...INVOICE_STATUSES].map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${
                statusFilter === status
                  ? 'bg-[#C8952A] text-white'
                  : 'text-[#b8c9e2] hover:text-white hover:bg-white/10'
              }`}
            >
              {status === 'all' ? 'All Invoices' : status}
            </button>
          ))}
        </div>

        {/* Search & Period */}
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#b8c9e2]" />
            <input
              type="text"
              placeholder="Search client, invoice #..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A] text-sm"
            />
          </div>
          <select
            value={periodFilter}
            onChange={(e) => setPeriodFilter(e.target.value)}
            className="px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
          >
            {PERIOD_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#234b8a] border border-white/20 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="invoices-table">
            <thead className="bg-[#1a3a6e]">
              <tr>
                <th className="text-left text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Invoice #</th>
                <th className="text-left text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Client</th>
                <th className="text-left text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Service</th>
                <th className="text-left text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Date</th>
                <th className="text-left text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Due Date</th>
                <th className="text-right text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Amount</th>
                <th className="text-right text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Paid</th>
                <th className="text-right text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Balance</th>
                <th className="text-center text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Status</th>
                <th className="text-center text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="10" className="text-center py-8 text-[#b8c9e2]">Loading...</td></tr>
              ) : filteredInvoices.length === 0 ? (
                <tr><td colSpan="10" className="text-center py-8 text-[#b8c9e2]">No invoices found</td></tr>
              ) : (
                filteredInvoices.map((inv) => (
                  <tr key={inv.id} className="border-t border-white/10 hover:bg-white/5">
                    <td className="px-4 py-3 text-white text-sm font-mono">{inv.invoice_number}</td>
                    <td className="px-4 py-3 text-white text-sm">{inv.client_name}</td>
                    <td className="px-4 py-3 text-[#b8c9e2] text-sm max-w-[200px] truncate">{inv.service_description}</td>
                    <td className="px-4 py-3 text-[#b8c9e2] text-sm">{formatDate(inv.invoice_date)}</td>
                    <td className="px-4 py-3 text-[#b8c9e2] text-sm">{formatDate(inv.due_date)}</td>
                    <td className="px-4 py-3 text-white text-sm text-right font-mono">{formatCurrency(inv.amount)}</td>
                    <td className="px-4 py-3 text-green-400 text-sm text-right font-mono">{formatCurrency(inv.amount_paid)}</td>
                    <td className="px-4 py-3 text-orange-400 text-sm text-right font-mono">{formatCurrency(inv.amount - inv.amount_paid)}</td>
                    <td className="px-4 py-3 text-center"><StatusBadge status={inv.status} type="invoice" /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        {inv.status !== 'Paid' && (
                          <>
                            <button
                              onClick={() => handleMarkPaid(inv.id)}
                              className="p-1.5 text-green-400 hover:bg-green-400/20 rounded"
                              title="Mark as Paid"
                            >
                              <Check size={16} />
                            </button>
                            <button
                              onClick={() => handleStripeCharge(inv)}
                              className="p-1.5 text-[#635BFF] hover:bg-[#635BFF]/20 rounded"
                              title="Charge via Stripe"
                            >
                              <CreditCard size={16} />
                            </button>
                            <button
                              onClick={() => handleSendReminder(inv)}
                              className="p-1.5 text-orange-400 hover:bg-orange-400/20 rounded"
                              title="Send Payment Reminder"
                            >
                              <Mail size={16} />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => { setEditingInvoice(inv); setShowModal(true); }}
                          className="p-1.5 text-[#b8c9e2] hover:text-white hover:bg-white/10 rounded"
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(inv.id)}
                          className="p-1.5 text-red-400 hover:bg-red-400/20 rounded"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Summary Row */}
        <div className="border-t-2 border-[#C8952A] bg-[#1a3a6e] px-4 py-3 flex items-center gap-6">
          <div className="flex-1">
            <span className="text-[#b8c9e2] text-xs uppercase">Total Invoiced</span>
            <p className="text-white font-semibold font-mono">{formatCurrency(summary.total)}</p>
          </div>
          <div className="flex-1">
            <span className="text-[#b8c9e2] text-xs uppercase">Total Paid</span>
            <p className="text-green-400 font-semibold font-mono">{formatCurrency(summary.paid)}</p>
          </div>
          <div className="flex-1">
            <span className="text-[#b8c9e2] text-xs uppercase">Outstanding</span>
            <p className="text-orange-400 font-semibold font-mono">{formatCurrency(summary.outstanding)}</p>
          </div>
          <div className="flex-1">
            <span className="text-[#b8c9e2] text-xs uppercase">Overdue</span>
            <p className="text-red-400 font-semibold font-mono">{formatCurrency(summary.overdue)}</p>
          </div>
        </div>
      </div>

      {/* Invoice Modal */}
      {showModal && (
        <InvoiceModal
          invoice={editingInvoice}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditingInvoice(null); }}
        />
      )}

      {/* Stripe Modal */}
      {showStripeModal && selectedInvoice && (
        <StripePaymentModal
          invoice={selectedInvoice}
          token={token}
          onClose={() => { setShowStripeModal(false); setSelectedInvoice(null); }}
          onSuccess={() => { setShowStripeModal(false); setSelectedInvoice(null); fetchInvoices(); }}
        />
      )}

      {/* Reminder Modal */}
      {showReminderModal && reminderInvoice && (
        <ReminderModal
          invoice={reminderInvoice}
          token={token}
          onClose={() => { setShowReminderModal(false); setReminderInvoice(null); }}
          onSuccess={() => { setShowReminderModal(false); setReminderInvoice(null); }}
        />
      )}
    </div>
  );
};

// ============== PAYMENTS RECEIVED ==============
export const PaymentsReceived = ({ token }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [methodFilter, setMethodFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('');
  const [invoices, setInvoices] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API}/admin/payments`;
      const params = new URLSearchParams();
      if (methodFilter && methodFilter !== 'all') params.append('method', methodFilter);
      if (periodFilter) params.append('period', periodFilter);
      if (params.toString()) url += `?${params.toString()}`;
      
      const [paymentsRes, invoicesRes] = await Promise.all([
        axios.get(url, { headers }),
        axios.get(`${API}/admin/invoices`, { headers })
      ]);
      setPayments(paymentsRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, [token, methodFilter, periodFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSave = async (data) => {
    try {
      await axios.post(`${API}/admin/payments`, data, { headers });
      toast.success('Payment recorded');
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;
    try {
      await axios.delete(`${API}/admin/payments/${id}`, { headers });
      toast.success('Payment deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };

  const filteredPayments = payments.filter(pay => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      pay.client_name?.toLowerCase().includes(search) ||
      pay.invoice_number?.toLowerCase().includes(search) ||
      pay.payment_method?.toLowerCase().includes(search)
    );
  });

  // Calculate summary
  const now = new Date();
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const summary = {
    total: filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0),
    thisMonth: filteredPayments.filter(p => p.date_received >= thisMonthStart).reduce((sum, p) => sum + (p.amount || 0), 0),
    count: filteredPayments.length
  };

  return (
    <div className="space-y-4" data-testid="payments-received">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Payments Received</h2>
          <p className="text-[#b8c9e2] text-sm">Record & track all income from clients</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#C8952A] text-white rounded hover:bg-[#b8862a] transition-colors"
          data-testid="record-payment-btn"
        >
          <Plus size={18} />
          Record Payment
        </button>
      </div>

      {/* Filters */}
      <div className="bg-[#234b8a] border border-white/20 rounded-lg p-4">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#b8c9e2]" />
            <input
              type="text"
              placeholder="Search client, invoice #, method..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A] text-sm"
            />
          </div>
          <select
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
            className="px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
          >
            <option value="all">All Methods</option>
            {PAYMENT_METHODS.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          <select
            value={periodFilter}
            onChange={(e) => setPeriodFilter(e.target.value)}
            className="px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
          >
            {PERIOD_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#234b8a] border border-white/20 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="payments-table">
            <thead className="bg-[#1a3a6e]">
              <tr>
                <th className="text-left text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Date</th>
                <th className="text-left text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Client</th>
                <th className="text-left text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Invoice #</th>
                <th className="text-right text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Amount</th>
                <th className="text-left text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Method</th>
                <th className="text-left text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Ref #</th>
                <th className="text-left text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Notes</th>
                <th className="text-center text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="text-center py-8 text-[#b8c9e2]">Loading...</td></tr>
              ) : filteredPayments.length === 0 ? (
                <tr><td colSpan="8" className="text-center py-8 text-[#b8c9e2]">No payments found</td></tr>
              ) : (
                filteredPayments.map((pay) => (
                  <tr key={pay.id} className="border-t border-white/10 hover:bg-white/5">
                    <td className="px-4 py-3 text-white text-sm font-mono">{formatDate(pay.date_received)}</td>
                    <td className="px-4 py-3 text-white text-sm">{pay.client_name}</td>
                    <td className="px-4 py-3 text-[#b8c9e2] text-sm font-mono">{pay.invoice_number || '-'}</td>
                    <td className="px-4 py-3 text-green-400 text-sm text-right font-mono">{formatCurrency(pay.amount)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded ${pay.payment_method === 'Stripe' ? 'bg-[#635BFF]/20 text-[#635BFF]' : 'bg-white/10 text-[#b8c9e2]'}`}>
                        {pay.payment_method}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[#b8c9e2] text-sm font-mono">{pay.reference_number || '-'}</td>
                    <td className="px-4 py-3 text-[#b8c9e2] text-sm max-w-[150px] truncate">{pay.notes || '-'}</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleDelete(pay.id)}
                        className="p-1.5 text-red-400 hover:bg-red-400/20 rounded"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Summary Row */}
        <div className="border-t-2 border-[#C8952A] bg-[#1a3a6e] px-4 py-3 flex items-center gap-6">
          <div className="flex-1">
            <span className="text-[#b8c9e2] text-xs uppercase">Total Received</span>
            <p className="text-green-400 font-semibold font-mono">{formatCurrency(summary.total)}</p>
          </div>
          <div className="flex-1">
            <span className="text-[#b8c9e2] text-xs uppercase">This Month</span>
            <p className="text-white font-semibold font-mono">{formatCurrency(summary.thisMonth)}</p>
          </div>
          <div className="flex-1">
            <span className="text-[#b8c9e2] text-xs uppercase">Transactions</span>
            <p className="text-white font-semibold">{summary.count}</p>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showModal && (
        <PaymentModal
          invoices={invoices}
          onSave={handleSave}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

// ============== BILLS & EXPENSES ==============
export const BillsExpenses = ({ token }) => {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchBills = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API}/admin/bills`;
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (categoryFilter && categoryFilter !== 'all') params.append('category', categoryFilter);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await axios.get(url, { headers });
      setBills(res.data);
    } catch (error) {
      toast.error('Failed to load bills');
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, categoryFilter]);

  useEffect(() => {
    fetchBills();
  }, [fetchBills]);

  const handleSave = async (data) => {
    try {
      if (editingBill) {
        await axios.put(`${API}/admin/bills/${editingBill.id}`, data, { headers });
        toast.success('Bill updated');
      } else {
        await axios.post(`${API}/admin/bills`, data, { headers });
        toast.success('Bill created');
      }
      setShowModal(false);
      setEditingBill(null);
      fetchBills();
    } catch (error) {
      toast.error('Failed to save bill');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this bill?')) return;
    try {
      await axios.delete(`${API}/admin/bills/${id}`, { headers });
      toast.success('Bill deleted');
      fetchBills();
    } catch (error) {
      toast.error('Failed to delete bill');
    }
  };

  const handleMarkPaid = async (id) => {
    try {
      await axios.post(`${API}/admin/bills/${id}/mark-paid`, {}, { headers });
      toast.success('Bill marked as paid');
      fetchBills();
    } catch (error) {
      toast.error('Failed to mark bill as paid');
    }
  };

  const filteredBills = bills.filter(bill => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      bill.payee?.toLowerCase().includes(search) ||
      bill.category?.toLowerCase().includes(search)
    );
  });

  // Calculate summary
  const summary = {
    total: filteredBills.reduce((sum, b) => sum + (b.amount || 0), 0),
    paid: filteredBills.filter(b => b.status === 'Paid').reduce((sum, b) => sum + (b.amount || 0), 0),
    unpaid: filteredBills.filter(b => b.status !== 'Paid').reduce((sum, b) => sum + (b.amount || 0), 0),
    overdue: filteredBills.filter(b => b.status === 'Overdue').reduce((sum, b) => sum + (b.amount || 0), 0),
    monthly: filteredBills.filter(b => b.frequency === 'Monthly' && b.status !== 'Paid').reduce((sum, b) => sum + (b.amount || 0), 0)
  };

  return (
    <div className="space-y-4" data-testid="bills-expenses">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Bills & Expenses</h2>
          <p className="text-[#b8c9e2] text-sm">Track current, upcoming & future bills</p>
        </div>
        <button
          onClick={() => { setEditingBill(null); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-[#C8952A] text-white rounded hover:bg-[#b8862a] transition-colors"
          data-testid="add-bill-btn"
        >
          <Plus size={18} />
          Add Bill
        </button>
      </div>

      {/* Filters */}
      <div className="bg-[#234b8a] border border-white/20 rounded-lg p-4">
        {/* Status Tabs */}
        <div className="flex gap-1 mb-4 border-b border-white/10 pb-3">
          {['all', ...BILL_STATUSES].map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${
                statusFilter === status
                  ? 'bg-[#C8952A] text-white'
                  : 'text-[#b8c9e2] hover:text-white hover:bg-white/10'
              }`}
            >
              {status === 'all' ? 'All Bills' : status}
            </button>
          ))}
        </div>

        {/* Search & Category */}
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#b8c9e2]" />
            <input
              type="text"
              placeholder="Search payee, category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A] text-sm"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
          >
            <option value="all">All Categories</option>
            {BILL_CATEGORIES.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#234b8a] border border-white/20 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="bills-table">
            <thead className="bg-[#1a3a6e]">
              <tr>
                <th className="text-left text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Payee</th>
                <th className="text-left text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Category</th>
                <th className="text-right text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Amount</th>
                <th className="text-left text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Due Date</th>
                <th className="text-left text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Frequency</th>
                <th className="text-center text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Status</th>
                <th className="text-left text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Notes</th>
                <th className="text-center text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="text-center py-8 text-[#b8c9e2]">Loading...</td></tr>
              ) : filteredBills.length === 0 ? (
                <tr><td colSpan="8" className="text-center py-8 text-[#b8c9e2]">No bills found</td></tr>
              ) : (
                filteredBills.map((bill) => (
                  <tr key={bill.id} className="border-t border-white/10 hover:bg-white/5">
                    <td className="px-4 py-3 text-white text-sm">{bill.payee}</td>
                    <td className="px-4 py-3 text-[#b8c9e2] text-sm">{bill.category}</td>
                    <td className="px-4 py-3 text-red-400 text-sm text-right font-mono">{formatCurrency(bill.amount)}</td>
                    <td className="px-4 py-3 text-[#b8c9e2] text-sm">{formatDate(bill.due_date)}</td>
                    <td className="px-4 py-3 text-[#b8c9e2] text-sm">{bill.frequency}</td>
                    <td className="px-4 py-3 text-center"><StatusBadge status={bill.status} type="bill" /></td>
                    <td className="px-4 py-3 text-[#b8c9e2] text-sm max-w-[150px] truncate">{bill.notes || '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        {bill.status !== 'Paid' && (
                          <button
                            onClick={() => handleMarkPaid(bill.id)}
                            className="p-1.5 text-green-400 hover:bg-green-400/20 rounded"
                            title="Mark as Paid"
                          >
                            <Check size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => { setEditingBill(bill); setShowModal(true); }}
                          className="p-1.5 text-[#b8c9e2] hover:text-white hover:bg-white/10 rounded"
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(bill.id)}
                          className="p-1.5 text-red-400 hover:bg-red-400/20 rounded"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Summary Row */}
        <div className="border-t-2 border-[#C8952A] bg-[#1a3a6e] px-4 py-3 flex items-center gap-6">
          <div className="flex-1">
            <span className="text-[#b8c9e2] text-xs uppercase">Total Bills</span>
            <p className="text-white font-semibold font-mono">{formatCurrency(summary.total)}</p>
          </div>
          <div className="flex-1">
            <span className="text-[#b8c9e2] text-xs uppercase">Paid</span>
            <p className="text-green-400 font-semibold font-mono">{formatCurrency(summary.paid)}</p>
          </div>
          <div className="flex-1">
            <span className="text-[#b8c9e2] text-xs uppercase">Unpaid</span>
            <p className="text-orange-400 font-semibold font-mono">{formatCurrency(summary.unpaid)}</p>
          </div>
          <div className="flex-1">
            <span className="text-[#b8c9e2] text-xs uppercase">Overdue</span>
            <p className="text-red-400 font-semibold font-mono">{formatCurrency(summary.overdue)}</p>
          </div>
          <div className="flex-1">
            <span className="text-[#b8c9e2] text-xs uppercase">Monthly Recurring</span>
            <p className="text-[#C8952A] font-semibold font-mono">{formatCurrency(summary.monthly)}</p>
          </div>
        </div>
      </div>

      {/* Bill Modal */}
      {showModal && (
        <BillModal
          bill={editingBill}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditingBill(null); }}
        />
      )}
    </div>
  );
};

// ============== BAD DEBT TRACKER ==============
export const BadDebtTracker = ({ token }) => {
  const [badDebts, setBadDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchBadDebts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/bad-debts`, { headers });
      setBadDebts(res.data);
    } catch (error) {
      toast.error('Failed to load bad debts');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchBadDebts();
  }, [fetchBadDebts]);

  const handleSave = async (data) => {
    try {
      await axios.post(`${API}/admin/bad-debts`, data, { headers });
      toast.success('Bad debt recorded');
      setShowModal(false);
      fetchBadDebts();
    } catch (error) {
      toast.error('Failed to record bad debt');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this bad debt record?')) return;
    try {
      await axios.delete(`${API}/admin/bad-debts/${id}`, { headers });
      toast.success('Bad debt deleted');
      fetchBadDebts();
    } catch (error) {
      toast.error('Failed to delete bad debt');
    }
  };

  const filteredBadDebts = badDebts.filter(bd => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return bd.client_name?.toLowerCase().includes(search);
  });

  // Calculate summary
  const summary = {
    total: filteredBadDebts.reduce((sum, bd) => sum + (bd.amount_written_off || 0), 0),
    count: filteredBadDebts.length,
    avg: filteredBadDebts.length > 0 ? filteredBadDebts.reduce((sum, bd) => sum + (bd.amount_written_off || 0), 0) / filteredBadDebts.length : 0
  };

  return (
    <div className="space-y-4" data-testid="bad-debt-tracker">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white">Bad Debt Tracker</h2>
          <p className="text-[#b8c9e2] text-sm">Uncollectible accounts & write-offs</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#C8952A] text-white rounded hover:bg-[#b8862a] transition-colors"
          data-testid="add-bad-debt-btn"
        >
          <Plus size={18} />
          Add Bad Debt
        </button>
      </div>

      {/* Search */}
      <div className="bg-[#234b8a] border border-white/20 rounded-lg p-4">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#b8c9e2]" />
          <input
            type="text"
            placeholder="Search client name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A] text-sm"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#234b8a] border border-white/20 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="bad-debts-table">
            <thead className="bg-[#1a3a6e]">
              <tr>
                <th className="text-left text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Client</th>
                <th className="text-left text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Original Invoice</th>
                <th className="text-right text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Original Amount</th>
                <th className="text-left text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Write-Off Date</th>
                <th className="text-right text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Amount Written Off</th>
                <th className="text-left text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Reason</th>
                <th className="text-center text-[#C8952A] text-xs font-semibold uppercase tracking-wider px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="text-center py-8 text-[#b8c9e2]">Loading...</td></tr>
              ) : filteredBadDebts.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-8 text-[#b8c9e2]">No bad debt records found</td></tr>
              ) : (
                filteredBadDebts.map((bd) => (
                  <tr key={bd.id} className="border-t border-white/10 hover:bg-white/5">
                    <td className="px-4 py-3 text-white text-sm">{bd.client_name}</td>
                    <td className="px-4 py-3 text-[#b8c9e2] text-sm font-mono">{bd.original_invoice_number || '-'}</td>
                    <td className="px-4 py-3 text-[#b8c9e2] text-sm text-right font-mono">{formatCurrency(bd.original_amount)}</td>
                    <td className="px-4 py-3 text-[#b8c9e2] text-sm">{formatDate(bd.write_off_date)}</td>
                    <td className="px-4 py-3 text-purple-400 text-sm text-right font-mono">{formatCurrency(bd.amount_written_off)}</td>
                    <td className="px-4 py-3 text-[#b8c9e2] text-sm">{bd.reason}</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleDelete(bd.id)}
                        className="p-1.5 text-red-400 hover:bg-red-400/20 rounded"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Summary Row */}
        <div className="border-t-2 border-[#C8952A] bg-[#1a3a6e] px-4 py-3 flex items-center gap-6">
          <div className="flex-1">
            <span className="text-[#b8c9e2] text-xs uppercase">Total Written Off</span>
            <p className="text-purple-400 font-semibold font-mono">{formatCurrency(summary.total)}</p>
          </div>
          <div className="flex-1">
            <span className="text-[#b8c9e2] text-xs uppercase">Number of Accounts</span>
            <p className="text-white font-semibold">{summary.count}</p>
          </div>
          <div className="flex-1">
            <span className="text-[#b8c9e2] text-xs uppercase">Avg Per Account</span>
            <p className="text-white font-semibold font-mono">{formatCurrency(summary.avg)}</p>
          </div>
        </div>
      </div>

      {/* Bad Debt Modal */}
      {showModal && (
        <BadDebtModal
          onSave={handleSave}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

// ============== P&L STATEMENT ==============
export const PLStatement = ({ token }) => {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('this_year');
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchPL = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/pl-statement?period=${period}`, { headers });
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load P&L statement');
    } finally {
      setLoading(false);
    }
  }, [token, period]);

  useEffect(() => {
    fetchPL();
  }, [fetchPL]);

  const handlePrint = () => {
    window.print();
  };

  if (loading && !data) {
    return <div className="text-white text-center py-12">Loading P&L statement...</div>;
  }

  return (
    <div className="space-y-4" data-testid="pl-statement">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-white">Profit & Loss Statement</h2>
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-3 py-2 bg-[#234b8a] border border-white/20 text-white rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
          >
            <option value="this_month">This Month</option>
            <option value="this_quarter">This Quarter</option>
            <option value="this_year">This Year</option>
            <option value="">All Time</option>
          </select>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded hover:bg-white/20 transition-colors text-sm"
          >
            <FileText size={16} />
            Print / Export
          </button>
        </div>
      </div>

      {/* P&L Content */}
      <div className="bg-[#234b8a] border border-white/20 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-white font-medium flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#C8952A]"></span>
            Statement of Income & Expenses
          </h3>
          <span className="text-[#C8952A] text-sm font-mono">{data?.period_label}</span>
        </div>

        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Income Section */}
          <div className="bg-[#1a3a6e] rounded-lg p-4">
            <h4 className="text-[#C8952A] text-sm font-semibold uppercase tracking-wider mb-4 pb-2 border-b border-[#C8952A]/30">
              Income / Revenue
            </h4>
            <div className="space-y-3">
              <PLRow label="Payments Received" value={data?.income?.payments_received} color="green" />
              <PLRow label="Total Invoiced" value={data?.income?.total_invoiced} />
              <PLRow label="Collected" value={data?.income?.collected} color="green" />
              <PLRow label="Outstanding" value={data?.income?.outstanding} color="orange" />
              <div className="border-t border-white/20 pt-3 mt-3">
                <PLRow label="Total Income" value={data?.income?.payments_received} bold color="green" />
              </div>
            </div>
          </div>

          {/* Expenses Section */}
          <div className="bg-[#1a3a6e] rounded-lg p-4">
            <h4 className="text-[#C8952A] text-sm font-semibold uppercase tracking-wider mb-4 pb-2 border-b border-[#C8952A]/30">
              Expenses / Bills
            </h4>
            <div className="space-y-3">
              {data?.expenses?.by_category && Object.keys(data.expenses.by_category).length > 0 ? (
                Object.entries(data.expenses.by_category).map(([cat, amt]) => (
                  <PLRow key={cat} label={cat} value={amt} color="red" />
                ))
              ) : (
                <p className="text-[#b8c9e2] text-sm">No expenses recorded</p>
              )}
              <div className="border-t border-white/20 pt-3 mt-3">
                <PLRow label="Total Expenses" value={data?.expenses?.total} bold color="red" />
              </div>
            </div>
          </div>

          {/* Gross Profit */}
          <div className="bg-[#1a3a6e] rounded-lg p-4">
            <h4 className="text-[#C8952A] text-sm font-semibold uppercase tracking-wider mb-4 pb-2 border-b border-[#C8952A]/30">
              Gross Profit
            </h4>
            <PLRow label="Income - Expenses" value={data?.gross_profit} bold color={data?.gross_profit >= 0 ? 'green' : 'red'} />
          </div>

          {/* Bad Debt */}
          <div className="bg-[#1a3a6e] rounded-lg p-4">
            <h4 className="text-[#C8952A] text-sm font-semibold uppercase tracking-wider mb-4 pb-2 border-b border-[#C8952A]/30">
              Bad Debt / Write-Offs
            </h4>
            <div className="space-y-3">
              {data?.bad_debt?.items?.length > 0 ? (
                data.bad_debt.items.map((item, i) => (
                  <PLRow key={i} label={`${item.client} (${item.invoice || 'N/A'})`} value={item.amount} color="purple" />
                ))
              ) : (
                <p className="text-[#b8c9e2] text-sm">No bad debt recorded</p>
              )}
              <div className="border-t border-white/20 pt-3 mt-3">
                <PLRow label="Total Bad Debt" value={data?.bad_debt?.total} bold color="purple" />
              </div>
            </div>
          </div>
        </div>

        {/* Net Profit/Loss */}
        <div className="mx-6 mb-6 bg-[#0F1950] border-2 border-[#C8952A] rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-white font-semibold text-lg">NET PROFIT / LOSS — {data?.period_label}</h4>
              <p className="text-[#b8c9e2] text-sm">Gross Profit minus Bad Debt</p>
            </div>
            <p className={`text-3xl font-bold font-mono ${data?.net_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatCurrency(data?.net_profit)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const PLRow = ({ label, value, bold, color }) => {
  const colorClasses = {
    green: 'text-green-400',
    red: 'text-red-400',
    orange: 'text-orange-400',
    purple: 'text-purple-400',
    default: 'text-white'
  };

  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${bold ? 'text-white font-semibold' : 'text-[#b8c9e2]'}`}>{label}</span>
      <span className={`text-sm font-mono ${bold ? 'font-semibold' : ''} ${colorClasses[color] || colorClasses.default}`}>
        {formatCurrency(value)}
      </span>
    </div>
  );
};

// ============== SHARED COMPONENTS ==============

const StatusBadge = ({ status, type }) => {
  const colors = {
    invoice: {
      Pending: 'bg-yellow-500/20 text-yellow-400',
      Paid: 'bg-green-500/20 text-green-400',
      Partial: 'bg-blue-500/20 text-blue-400',
      Overdue: 'bg-red-500/20 text-red-400'
    },
    bill: {
      Current: 'bg-blue-500/20 text-blue-400',
      Upcoming: 'bg-yellow-500/20 text-yellow-400',
      Future: 'bg-purple-500/20 text-purple-400',
      Paid: 'bg-green-500/20 text-green-400',
      Overdue: 'bg-red-500/20 text-red-400'
    }
  };

  const colorClass = colors[type]?.[status] || 'bg-gray-500/20 text-gray-400';

  return (
    <span className={`text-xs px-2 py-1 rounded ${colorClass}`}>
      {status}
    </span>
  );
};

// ============== MODALS ==============

const InvoiceModal = ({ invoice, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    client_name: invoice?.client_name || '',
    service_description: invoice?.service_description || '',
    invoice_date: invoice?.invoice_date || new Date().toISOString().split('T')[0],
    due_date: invoice?.due_date || '',
    amount: invoice?.amount || '',
    amount_paid: invoice?.amount_paid || 0,
    status: invoice?.status || 'Pending',
    notes: invoice?.notes || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      amount: parseFloat(formData.amount),
      amount_paid: parseFloat(formData.amount_paid) || 0
    });
  };

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
      <div className="bg-[#234b8a] border border-[#C8952A] rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-[#C8952A] font-semibold text-lg">
            {invoice ? 'Edit Invoice' : 'New Invoice'}
          </h3>
          <button onClick={onClose} className="text-[#b8c9e2] hover:text-white">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm text-[#b8c9e2] mb-1">Client Name *</label>
              <input
                type="text"
                required
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              />
            </div>
            <div>
              <label className="block text-sm text-[#b8c9e2] mb-1">Invoice Date *</label>
              <input
                type="date"
                required
                value={formData.invoice_date}
                onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              />
            </div>
            <div>
              <label className="block text-sm text-[#b8c9e2] mb-1">Due Date *</label>
              <input
                type="date"
                required
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm text-[#b8c9e2] mb-1">Service Description *</label>
              <input
                type="text"
                required
                value={formData.service_description}
                onChange={(e) => setFormData({ ...formData, service_description: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              />
            </div>
            <div>
              <label className="block text-sm text-[#b8c9e2] mb-1">Amount ($) *</label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              />
            </div>
            <div>
              <label className="block text-sm text-[#b8c9e2] mb-1">Amount Paid ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.amount_paid}
                onChange={(e) => setFormData({ ...formData, amount_paid: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              />
            </div>
            <div>
              <label className="block text-sm text-[#b8c9e2] mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              >
                {INVOICE_STATUSES.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm text-[#b8c9e2] mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A] resize-none"
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-[#b8c9e2] hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#C8952A] text-white rounded hover:bg-[#b8862a] transition-colors"
            >
              Save Invoice
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PaymentModal = ({ invoices, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    date_received: new Date().toISOString().split('T')[0],
    client_name: '',
    invoice_number: '',
    invoice_id: '',
    amount: '',
    payment_method: 'Check',
    reference_number: '',
    notes: ''
  });

  const handleInvoiceSelect = (invoiceId) => {
    const inv = invoices.find(i => i.id === invoiceId);
    if (inv) {
      setFormData({
        ...formData,
        invoice_id: inv.id,
        invoice_number: inv.invoice_number,
        client_name: inv.client_name,
        amount: inv.amount - inv.amount_paid
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      amount: parseFloat(formData.amount)
    });
  };

  const pendingInvoices = invoices.filter(i => i.status !== 'Paid');

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
      <div className="bg-[#234b8a] border border-[#C8952A] rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-[#C8952A] font-semibold text-lg">Record Payment</h3>
          <button onClick={onClose} className="text-[#b8c9e2] hover:text-white">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-[#b8c9e2] mb-1">Date Received *</label>
              <input
                type="date"
                required
                value={formData.date_received}
                onChange={(e) => setFormData({ ...formData, date_received: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              />
            </div>
            <div>
              <label className="block text-sm text-[#b8c9e2] mb-1">Link to Invoice</label>
              <select
                value={formData.invoice_id}
                onChange={(e) => handleInvoiceSelect(e.target.value)}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              >
                <option value="">-- Select Invoice --</option>
                {pendingInvoices.map(inv => (
                  <option key={inv.id} value={inv.id}>
                    {inv.invoice_number} - {inv.client_name} ({formatCurrency(inv.amount - inv.amount_paid)})
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm text-[#b8c9e2] mb-1">Client Name *</label>
              <input
                type="text"
                required
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              />
            </div>
            <div>
              <label className="block text-sm text-[#b8c9e2] mb-1">Amount ($) *</label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              />
            </div>
            <div>
              <label className="block text-sm text-[#b8c9e2] mb-1">Payment Method *</label>
              <select
                required
                value={formData.payment_method}
                onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              >
                {PAYMENT_METHODS.map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-[#b8c9e2] mb-1">Reference / Check #</label>
              <input
                type="text"
                value={formData.reference_number}
                onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm text-[#b8c9e2] mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A] resize-none"
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-[#b8c9e2] hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#C8952A] text-white rounded hover:bg-[#b8862a] transition-colors"
            >
              Record Payment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BillModal = ({ bill, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    payee: bill?.payee || '',
    category: bill?.category || 'Miscellaneous',
    amount: bill?.amount || '',
    due_date: bill?.due_date || '',
    frequency: bill?.frequency || 'One-Time',
    status: bill?.status || 'Current',
    notes: bill?.notes || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      amount: parseFloat(formData.amount)
    });
  };

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
      <div className="bg-[#234b8a] border border-[#C8952A] rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-[#C8952A] font-semibold text-lg">
            {bill ? 'Edit Bill' : 'Add Bill'}
          </h3>
          <button onClick={onClose} className="text-[#b8c9e2] hover:text-white">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm text-[#b8c9e2] mb-1">Payee / Vendor *</label>
              <input
                type="text"
                required
                value={formData.payee}
                onChange={(e) => setFormData({ ...formData, payee: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              />
            </div>
            <div>
              <label className="block text-sm text-[#b8c9e2] mb-1">Category *</label>
              <select
                required
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              >
                {BILL_CATEGORIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-[#b8c9e2] mb-1">Amount ($) *</label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              />
            </div>
            <div>
              <label className="block text-sm text-[#b8c9e2] mb-1">Due Date *</label>
              <input
                type="date"
                required
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              />
            </div>
            <div>
              <label className="block text-sm text-[#b8c9e2] mb-1">Frequency</label>
              <select
                value={formData.frequency}
                onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              >
                {BILL_FREQUENCIES.map(f => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-[#b8c9e2] mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              >
                {BILL_STATUSES.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm text-[#b8c9e2] mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A] resize-none"
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-[#b8c9e2] hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#C8952A] text-white rounded hover:bg-[#b8862a] transition-colors"
            >
              Save Bill
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BadDebtModal = ({ onSave, onClose }) => {
  const [formData, setFormData] = useState({
    client_name: '',
    original_invoice_number: '',
    original_amount: '',
    amount_written_off: '',
    write_off_date: new Date().toISOString().split('T')[0],
    reason: 'Client Unresponsive',
    notes: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      original_amount: parseFloat(formData.original_amount),
      amount_written_off: parseFloat(formData.amount_written_off)
    });
  };

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
      <div className="bg-[#234b8a] border border-[#C8952A] rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-[#C8952A] font-semibold text-lg">Add Bad Debt</h3>
          <button onClick={onClose} className="text-[#b8c9e2] hover:text-white">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm text-[#b8c9e2] mb-1">Client Name *</label>
              <input
                type="text"
                required
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              />
            </div>
            <div>
              <label className="block text-sm text-[#b8c9e2] mb-1">Original Invoice #</label>
              <input
                type="text"
                value={formData.original_invoice_number}
                onChange={(e) => setFormData({ ...formData, original_invoice_number: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              />
            </div>
            <div>
              <label className="block text-sm text-[#b8c9e2] mb-1">Original Amount ($) *</label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={formData.original_amount}
                onChange={(e) => setFormData({ ...formData, original_amount: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              />
            </div>
            <div>
              <label className="block text-sm text-[#b8c9e2] mb-1">Amount Written Off ($) *</label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={formData.amount_written_off}
                onChange={(e) => setFormData({ ...formData, amount_written_off: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              />
            </div>
            <div>
              <label className="block text-sm text-[#b8c9e2] mb-1">Write-Off Date *</label>
              <input
                type="date"
                required
                value={formData.write_off_date}
                onChange={(e) => setFormData({ ...formData, write_off_date: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm text-[#b8c9e2] mb-1">Reason *</label>
              <select
                required
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A]"
              >
                {BAD_DEBT_REASONS.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm text-[#b8c9e2] mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#C8952A] resize-none"
              />
            </div>
          </div>
          <div className="flex gap-3 justify-end pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-[#b8c9e2] hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              Write Off
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const StripePaymentModal = ({ invoice, token, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('link'); // 'link' or 'charge'
  const [clientEmail, setClientEmail] = useState('');
  const [paymentLinkUrl, setPaymentLinkUrl] = useState('');

  const headers = { Authorization: `Bearer ${token}` };
  const amountDue = invoice.amount - invoice.amount_paid;

  const handleSendLink = async () => {
    try {
      setLoading(true);
      const res = await axios.post(
        `${API}/admin/stripe/create-payment-link?invoice_id=${invoice.id}${clientEmail ? `&client_email=${clientEmail}` : ''}`,
        {},
        { headers }
      );
      setPaymentLinkUrl(res.data.payment_link_url);
      if (res.data.email_sent) {
        toast.success('Payment link sent to client email');
      } else {
        toast.success('Payment link created');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create payment link');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(paymentLinkUrl);
    toast.success('Payment link copied to clipboard');
  };

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
      <div className="bg-[#234b8a] border border-[#635BFF] rounded-lg w-full max-w-md">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-[#635BFF] font-semibold text-lg flex items-center gap-2">
            <CreditCard size={20} />
            Stripe Payment
          </h3>
          <button onClick={onClose} className="text-[#b8c9e2] hover:text-white">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {/* Invoice Info */}
          <div className="bg-[#1a3a6e] rounded-lg p-4">
            <p className="text-[#b8c9e2] text-sm">Invoice</p>
            <p className="text-white font-mono">{invoice.invoice_number}</p>
            <p className="text-white text-sm mt-1">{invoice.client_name}</p>
            <p className="text-[#635BFF] text-xl font-bold mt-2">{formatCurrency(amountDue)} due</p>
          </div>

          {paymentLinkUrl ? (
            <div className="space-y-3">
              <p className="text-green-400 text-sm">Payment link created successfully!</p>
              <div className="bg-[#1a3a6e] rounded p-3">
                <p className="text-[#b8c9e2] text-xs mb-1">Payment Link:</p>
                <p className="text-white text-sm break-all font-mono">{paymentLinkUrl}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={copyLink}
                  className="flex-1 px-4 py-2 bg-[#635BFF] text-white rounded hover:bg-[#5851ea] transition-colors"
                >
                  Copy Link
                </button>
                <button
                  onClick={() => window.open(paymentLinkUrl, '_blank')}
                  className="flex-1 px-4 py-2 bg-white/10 text-white rounded hover:bg-white/20 transition-colors flex items-center justify-center gap-2"
                >
                  Open <ExternalLink size={14} />
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-[#b8c9e2] mb-1">Client Email (optional)</label>
                <input
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  placeholder="client@example.com"
                  className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-[#635BFF]"
                />
                <p className="text-[#b8c9e2] text-xs mt-1">If provided, payment link will be emailed to client</p>
              </div>
              <button
                onClick={handleSendLink}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#635BFF] text-white rounded hover:bg-[#5851ea] transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <RefreshCw size={18} className="animate-spin" />
                ) : (
                  <Send size={18} />
                )}
                {clientEmail ? 'Send Payment Link' : 'Generate Payment Link'}
              </button>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-white/10 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[#b8c9e2] hover:text-white transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ReminderModal = ({ invoice, token, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [clientEmail, setClientEmail] = useState('');
  const [sent, setSent] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const amountDue = invoice.amount - invoice.amount_paid;
  const today = new Date().toISOString().split('T')[0];
  const isOverdue = invoice.due_date < today;

  const handleSendReminder = async () => {
    if (!clientEmail) {
      toast.error('Please enter client email address');
      return;
    }
    
    try {
      setLoading(true);
      const res = await axios.post(
        `${API}/admin/invoices/${invoice.id}/send-reminder?client_email=${encodeURIComponent(clientEmail)}`,
        {},
        { headers }
      );
      toast.success(res.data.message);
      setSent(true);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
      <div className="bg-[#234b8a] border border-orange-500 rounded-lg w-full max-w-md">
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-orange-400 font-semibold text-lg flex items-center gap-2">
            <Mail size={20} />
            Send Payment Reminder
          </h3>
          <button onClick={onClose} className="text-[#b8c9e2] hover:text-white">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {/* Invoice Info */}
          <div className="bg-[#1a3a6e] rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[#b8c9e2] text-sm">Invoice</p>
              {isOverdue && (
                <span className="text-xs px-2 py-1 bg-red-500/20 text-red-400 rounded">OVERDUE</span>
              )}
            </div>
            <p className="text-white font-mono">{invoice.invoice_number}</p>
            <p className="text-white text-sm mt-1">{invoice.client_name}</p>
            <div className="mt-3 pt-3 border-t border-white/10 flex justify-between">
              <div>
                <p className="text-[#b8c9e2] text-xs">Due Date</p>
                <p className={`text-sm ${isOverdue ? 'text-red-400' : 'text-white'}`}>{formatDate(invoice.due_date)}</p>
              </div>
              <div className="text-right">
                <p className="text-[#b8c9e2] text-xs">Amount Due</p>
                <p className="text-orange-400 text-lg font-bold">{formatCurrency(amountDue)}</p>
              </div>
            </div>
          </div>

          {sent ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Check size={24} className="text-green-400" />
              </div>
              <p className="text-green-400 font-medium">Reminder Sent!</p>
              <p className="text-[#b8c9e2] text-sm mt-1">Email has been sent to {clientEmail}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-[#b8c9e2] mb-1">Client Email *</label>
                <input
                  type="email"
                  required
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  placeholder="client@example.com"
                  className="w-full px-3 py-2 bg-[#1a3a6e] border border-white/20 text-white rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div className="bg-orange-500/10 border border-orange-500/30 rounded p-3">
                <p className="text-orange-400 text-sm flex items-start gap-2">
                  <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                  {isOverdue 
                    ? "This invoice is overdue. The reminder will include an urgency notice."
                    : "A friendly payment reminder will be sent with invoice details."}
                </p>
              </div>
              <button
                onClick={handleSendReminder}
                disabled={loading || !clientEmail}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <RefreshCw size={18} className="animate-spin" />
                ) : (
                  <Send size={18} />
                )}
                Send Reminder Email
              </button>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-white/10 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-[#b8c9e2] hover:text-white transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default FinancialDashboard;
